import React, { useEffect, useState } from "react";
import { Pencil, PlusCircle, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// APIs
import { getAllStudents } from "../../../services/operations/authAPI";
import {
  getStudentFees,
  createStudentFees,
  payStudentFees,
} from "../../../services/operations/studentFeesAPI";

const ProgressBar = ({ paid = 0, total = 0 }) => {
  const percent = total ? Math.round((paid / total) * 100) : 0;

  return (
    <div className="flex items-center gap-2">
      <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
        <div
          className={`h-2 rounded-full ${
            percent === 100 ? "bg-green-500" : percent >= 50 ? "bg-yellow-500" : "bg-red-500"
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-sm font-medium">{percent}%</span>
    </div>
  );
};

const StudentFees = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editStudent, setEditStudent] = useState(null);
  const [selectedYear, setSelectedYear] = useState("2025-2026");
  const years = ["2025-2026", "2026-2027", "2027-2028"];

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getAllStudents());
      const studentsList = response?.students || [];

      const enriched = await Promise.all(
        studentsList.map(async (student) => {
          const feesRes = await getStudentFees(student._id);
          const latestFees = feesRes?.list?.[0] || null;
          return { ...student, fees: latestFees };
        })
      );

      setStudents(enriched);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    try {
      if (!editStudent.fees) {
        // CREATE new fees
        await createStudentFees({
          studentId: editStudent._id,
          academicYear: selectedYear,
          totalFees: editStudent.totalFees,
          discount: editStudent.discount || 0,
          paidAmount: editStudent.paidAmount || 0,
        });
      } else {
        // PAY existing fees
        await payStudentFees({
          studentFeesId: editStudent.fees._id,
          amount: editStudent.payAmount,
        });
      }

      toast.success("Fees updated successfully");
      setEditStudent(null);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Operation failed");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Student Fees</h1>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-4">Student</th>
              <th className="p-4">Class</th>
              <th className="p-4">Year</th>
              <th className="p-4">Total Fees</th>
              <th className="p-4">Discount</th>
              <th className="p-4">Payable</th>
              <th className="p-4">Paid</th>
              <th className="p-4">Pending</th>
              <th className="p-4">Reminder</th>
              <th className="p-4">Progress</th>
              <th className="p-4">Status</th>
              <th className="p-4">Action</th>
            </tr>
          </thead>

          <tbody>
            {students.map((s) => {
              const total = s.fees?.totalFees ?? 0;
              const discount = s.fees?.discount ?? 0;
              const payable = s.fees?.payableAmount ?? 0;
              const paid = s.fees?.paidAmount ?? 0;
              const pending = s.fees?.pendingAmount ?? 0;
              const reminder = s.fees?.reminderCount ?? 0;
              const status = s.fees?.status ?? "NOT SET";

              return (
                <tr
                  key={s._id}
                  className="border-t cursor-pointer hover:bg-gray-50"
                  onClick={() =>
                    navigate(`/dashboard/admin/fees-management/student-fees/fees-details/${s._id}`)
                  }
                >
                  <td className="p-4">{s.name}</td>
                  <td className="p-4">{s.class}</td>
                  <td className="p-4">{s.fees?.academicYear ?? "-"}</td>
                  <td className="p-4">₹{total}</td>
                  <td className="p-4">{discount}%</td>
                  <td className="p-4">₹{payable}</td>
                  <td className="p-4 text-green-600">₹{paid}</td>
                  <td className="p-4 text-red-600">₹{pending}</td>
                  <td className="p-4">{reminder}</td>
                  <td className="p-4 w-64">
                    <ProgressBar paid={paid} total={payable} />
                  </td>
                  <td className="p-4 font-semibold">{status}</td>
                  <td className="p-4">
                    {s.fees ? (
                      <Pencil
                        className="w-4 h-4 text-blue-600 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditStudent(s);
                          setSelectedYear(s.fees.academicYear ?? "2025-2026");
                        }}
                      />
                    ) : (
                      <PlusCircle
                        className="w-5 h-5 text-green-600 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditStudent(s);
                          setSelectedYear("2025-2026");
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {editStudent && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-96 space-y-4">
            <h2 className="text-xl font-bold">{editStudent.fees ? "Pay Fees" : "Create Fees"}</h2>

            {!editStudent.fees ? (
              <>
                <label className="block text-sm font-medium">Academic Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="border w-full px-3 py-2 rounded-lg"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  placeholder="Total Fees"
                  className="border w-full px-3 py-2 rounded-lg"
                  onChange={(e) =>
                    setEditStudent({ ...editStudent, totalFees: Number(e.target.value) })
                  }
                  value={editStudent.totalFees ?? ""}
                />
                <input
                  type="number"
                  placeholder="Discount"
                  className="border w-full px-3 py-2 rounded-lg"
                  onChange={(e) =>
                    setEditStudent({ ...editStudent, discount: Number(e.target.value) })
                  }
                  value={editStudent.discount ?? ""}
                />
                <input
                  type="number"
                  placeholder="Paid Amount (optional)"
                  className="border w-full px-3 py-2 rounded-lg"
                  onChange={(e) =>
                    setEditStudent({ ...editStudent, paidAmount: Number(e.target.value) })
                  }
                  value={editStudent.paidAmount ?? ""}
                />
              </>
            ) : (
              <input
                type="number"
                placeholder="Pay Amount"
                className="border w-full px-3 py-2 rounded-lg"
                onChange={(e) =>
                  setEditStudent({ ...editStudent, payAmount: Number(e.target.value) })
                }
              />
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEditStudent(null)}
                className="border px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentFees;
